import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import {
  Alert,
  Avatar,
  Box,
  BoxProps,
  Collapse,
  Divider,
  Grid,
  GridProps,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material'
import { green, grey } from '@mui/material/colors'
import { SnackbarCloseReason } from '@mui/material/Snackbar'
import { Form, Formik, FormikHelpers } from 'formik'
import {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { InferType, object, string } from 'yup'

import API from '../../api/baseUrl'
import pathUrl from '../../api/pathURL'
import { useAuthContext } from '../../auth/hooks/useAuthContext'
import { SnackBarAlertType, TextDocumentProps } from '../../types/interface'
import SubmitButton from '../forms/SubmitButtonWrapper'
import Textfield from '../forms/TextfieldWrapper'

interface DocumentStatusType {
  disable: boolean
  icon: ReactElement
  message: null | string | undefined
  style: object
}

const TextDocs = (props: Partial<TextDocumentProps> = {}) => {
  const auth = useAuthContext()
  const { t } = useTranslation()
  const { data } = props
  const [open, setOpen] = useState<boolean>(false)
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })
  const [status, setStatus] = useState<DocumentStatusType>({
    disable: true,
    icon: <PriorityHighIcon />,
    message: 'Not submitted',
    style: { backgroundColor: grey[100], color: grey[500] },
  })
  const componentIsMounted = useRef(true)

  const validationSchema = object().shape({
    // Simple validation schema for text fields only
    key: string()
      .nullable()
      .required(`${t('documents.validations.enter_required_field')}`),
  })

  const handleSubmit = async (
    values: InferType<typeof validationSchema>,
    actions: FormikHelpers<InferType<typeof validationSchema>>
  ) => {
    const body = {
      hst_number: values.key,
    }

    try {
      const tokenSource = await auth.getAccessJwtToken()
      const response = await API.put(pathUrl.updateProfile, body, {
        headers: {
          ...(import.meta.env.MODE ? { env: import.meta.env.MODE! } : {}),
          Authorization: tokenSource as unknown as string,
        },
      })
      const success = await response.status
      if (success === 200) {
        actions.setSubmitting(false)
        // Success - document submitted
        setStatus({
          disable: true,
          icon: <ThumbUpAltIcon />,
          message: 'Submitted',
          style: { backgroundColor: green[100], color: green[500] },
        })
        setOpen(!open)
      } else {
        setOpen(open)
        // display error message
        setAlertEvent({
          message: 'Error to submit Document',
          open: true,
          type: 'error',
        })
      }
    } catch (error) {
      actions.setSubmitting(false)
      setAlertEvent({
        message: 'An error occurred while submitting the document',
        open: true,
        type: 'error',
      })
    }
  }

  const handleCollapse = () => {
    setOpen(!open)
  }

  const handleCloseAlert = (
    _: Event | SyntheticEvent,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return
    setAlertEvent((prev) => ({ ...prev, open: false }))
  }

  const statusManager = (isSubmitted: boolean | undefined) => {
    if (isSubmitted) {
      setStatus({
        disable: true,
        icon: <ThumbUpAltIcon />,
        message: 'Submitted',
        style: { backgroundColor: green[100], color: green[500] },
      })
    } else {
      setStatus({
        disable: false,
        icon: <PriorityHighIcon />,
        message: 'Not submitted',
        style: { backgroundColor: grey[100], color: grey[500] },
      })
    }
  }

  const configGridContainer: GridProps = {
    container: true,
    rowGap: 4,
    sx: {
      backgroundColor: '#FAF9F9',
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      mb: 2,
      p: 4,
    },
  }

  const configGridItem: GridProps = {
    item: true,
    xs: 12,
  }

  const boxConfig: BoxProps = {
    sx: {
      backgroundColor: '#FAF9F9',
      borderBottomLeftRadius: 16,
      borderBottomRightRadius: 16,
      mb: 2,
      mt: -2,
      p: 4,
    },
  }

  const initialValues = {
    key: data?.value || '',
  } as InferType<typeof validationSchema>

  useEffect(() => {
    if (componentIsMounted.current) {
      statusManager(data?.is_submitted)
    }
    return () => {
      componentIsMounted.current = false
    }
  }, [])
  return (
    <>
      <ListItemButton disabled={status.disable} onClick={handleCollapse}>
        <ListItemAvatar>
          <Avatar sx={status.style} variant="circular">
            {status.icon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography color="text.primary" variant="subtitle1">
              {data?.text}
            </Typography>
          }
          secondary={
            <Typography color="text.secondary" variant="body2">
              {status.message}
            </Typography>
          }
        />
        {!status.disable && !open && <ExpandMoreIcon />}
        {!status.disable && open && <ExpandLessIcon />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          <Form>
            <Grid {...configGridContainer}>
              <Grid {...configGridItem}>
                <Textfield
                  label={
                    data?.key === 'hst_number'
                      ? t('documents.extra_documents.hst_number')
                      : t('documents.extra_documents.enter_value')
                  }
                  name="key"
                />
              </Grid>
            </Grid>
            <Box {...boxConfig}>
              <SubmitButton fullWidth>{`Submit ${data?.text}`}</SubmitButton>
            </Box>
          </Form>
        </Formik>
      </Collapse>
      <Divider />
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleCloseAlert}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={6}
          onClose={handleCloseAlert}
          severity={alertEvent.type}
          sx={{ width: '100%' }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default TextDocs
